import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "../api/axios";
import { RequiredActionsContext } from "./RequiredActionsContext";
import { EvaluationContext } from "./EvaluationContext";
import { CheckinContext } from "./CheckinContext";
import CityLaunchModal from "../components/events/CityLaunchModal";

export const CityLaunchContext = createContext({
  eligible: false,
  applied: false,
  hasPriority: false,
  setCatalogReady: () => {},
  openLaunch: () => {},
});

export function CityLaunchProvider({ children }) {
  const required = useContext(RequiredActionsContext);
  const evaluation = useContext(EvaluationContext);
  const checkin = useContext(CheckinContext);
  const [catalogReady, setCatalogReady] = useState(false);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const claiming = useRef(false);
  const submitting = useRef(false);
  const wasVisible = useRef(false);
  const [autoFailed, setAutoFailed] = useState(false);

  const blocked = Boolean(
    required.loading || required.open
    || evaluation?.loading || evaluation?.hasBlocking || (!open && checkin?.showModal)
  );
  const hasPriority = Boolean(catalogReady && data?.eligible && !blocked && (open || (!data.seen && !autoFailed)));

  useEffect(() => {
    if (open && hasPriority) wasVisible.current = true;
    if (!catalogReady && wasVisible.current) {
      wasVisible.current = false;
      setOpen(false);
    }
  }, [catalogReady, hasPriority, open]);

  useEffect(() => {
    if (!catalogReady || data) return undefined;
    let active = true;
    api.get("/accounts/city-launch/", { meta: { suppressGlobalError: true } })
      .then(({ data: payload }) => { if (active) setData(payload); })
      .catch((err) => { console.warn("[CityLaunch] status unavailable", err); });
    return () => { active = false; };
  }, [catalogReady, data]);

  useEffect(() => {
    if (!hasPriority || open || claiming.current || autoFailed) return;
    claiming.current = true;
    // Claim only from the plain catalog. GET never consumes the one-time welcome.
    api.post("/accounts/city-launch/", { action: "seen" }, { meta: { suppressGlobalError: true } })
      .then(({ data: payload }) => {
        setData(payload);
        if (payload.show) setOpen(true);
      })
      .catch((err) => {
        setAutoFailed(true);
        console.warn("[CityLaunch] welcome acknowledgement failed", err);
      })
      .finally(() => { claiming.current = false; });
  }, [autoFailed, hasPriority, open]);

  const openLaunch = useCallback(() => {
    if (!data?.eligible) return;
    setError(false);
    setAutoFailed(true);
    setOpen(true);
    // A deliberate banner click can reopen the welcome after the automatic view.
    api.post("/accounts/city-launch/", { action: "seen" }, { meta: { suppressGlobalError: true } })
      .then(({ data: payload }) => setData(payload))
      .catch((err) => { console.warn("[CityLaunch] welcome acknowledgement failed", err); });
  }, [data?.eligible]);

  const apply = async () => {
    if (submitting.current || data?.applied) return;
    submitting.current = true;
    setSaving(true);
    setError(false);
    try {
      const { data: payload } = await api.post("/accounts/city-launch/", { action: "apply" }, { meta: { suppressGlobalError: true } });
      setData(payload);
    } catch {
      setError(true);
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };

  return (
    <CityLaunchContext.Provider value={{
      city: data?.city, eligible: Boolean(data?.eligible), applied: Boolean(data?.applied), hasPriority,
      setCatalogReady, openLaunch,
    }}>
      {children}
      <CityLaunchModal
        open={open && hasPriority}
        city={data?.city}
        launched={Boolean(data?.launched)}
        applied={Boolean(data?.applied)}
        saving={saving}
        error={error}
        onApply={apply}
        onClose={() => { setOpen(false); setError(false); }}
      />
    </CityLaunchContext.Provider>
  );
}
